/**
 * client/src/lib/vault-key.ts — Vault KEK lifecycle management
 *
 * Two approved KEK derivation paths (L0-6 / Rule 12):
 *
 *   PRIMARY:   WebAuthn PRF output → HKDF → KEK
 *              Used on every normal sign-in (no password, no PBKDF2).
 *
 *   RECOVERY:  Guardian SSS 2-of-3 share reconstruction of the vault KEK.
 *              Performed in GuardianRecoveryFlow — not in this module.
 *              Reconstruction yields the same KEK that encrypts vault data.
 *
 * The vault KEK is an in-memory CryptoKey (non-extractable where possible).
 * It is wiped by clearActiveKek() when a request surfaces a session-death 401
 * (the server's inactivity timeout or absolute cap — INACTIVITY_TIMEOUT_MS /
 * MAX_SESSION_MS in shared/session-policy.ts, matched in queryClient.ts via
 * shared/session-death-codes.ts) or when ensure-active-kek.ts finds the slot
 * tagged to another user. Sign-out drops it by full-page navigation
 * (logout.ts). There is NO client-side idle timer of its own — the server's
 * clocks are the only clocks, so never restate their minute values here.
 *
 * NO PASSWORDS — no password string ever derives a KEK (L0-6 / Rule 11 / Rule 12).
 *
 * OWNER TAG: the in-memory slot also records WHOSE KEK it holds. Every
 * storeActiveKek caller passes the authenticated user id its flow derived the
 * KEK for (session-confirmed promotion, registration ceremony, guardian
 * recovery); identity-sensitive probe paths (ensure-active-kek) compare
 * getActiveKekOwner() against the CURRENT session user before blessing the
 * slot. This closes the shared-browser tab-swap gap where user A's KEK could
 * survive in tab memory and be wrapped to user B's guardians after B signs in
 * via another tab (refs #1007). The tag is an id string — never key material.
 *
 * EXISTENCE PROBES: call sites that only need a yes/no answer (does the slot
 * hold a KEK right now?) MUST use `hasActiveKek()`, not `getActiveKek()`.
 * `getActiveKek()` allocates and returns a live copy of the key material on
 * every call — using it purely as a truthy check materializes key bytes with
 * no plan to wipe them (#1016).
 *
 * (NIST CSF PR.DS-01 / NIST SP 800-131Ar2)
 */

import { hkdf }                 from "@noble/hashes/hkdf";
import { sha256 }               from "@noble/hashes/sha256";
import {
  derivePrfVaultKek,
  wrapKekWithPrf,
  unwrapKekWithPrf,
  atomicWipe,
} from "./prf-key-derivation";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KEK_BYTES         = 32;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VaultKekResult {
  /** 32-byte vault KEK — wipe with atomicWipe() after use */
  kek:         Uint8Array;
  /** AES-GCM wrapped KEK for server storage (iv:ciphertext:tag hex) */
  wrappedKek:  string;
}

// ---------------------------------------------------------------------------
// PRIMARY PATH: PRF-derived KEK
// ---------------------------------------------------------------------------

/**
 * Derive the vault KEK from the WebAuthn PRF output.
 *
 * Call this immediately after a successful WebAuthn assertion that evaluated
 * the PRF extension.  The prfOutput is wiped inside derivePrfVaultKek (L0-9).
 *
 * Returns:
 *   kek         — 32-byte KEK for in-memory vault decryption
 *   wrappedKek  — AES-GCM(KEK, HKDF(prfOutput)) for server storage
 *                 The prfOutput used for wrapping is a second copy derived
 *                 from the same assertion output (the original is wiped after
 *                 the HKDF step).
 *
 * @param prfOutput  Uint8Array from WebAuthn PRF extension. CONSUMED (wiped).
 * @param prfSalt    The user's stored PRF eval salt (hex string).
 */
export async function deriveKekFromPrf(
  prfOutput: Uint8Array,
  prfSalt:   string
): Promise<VaultKekResult> {
  // We need the prfOutput twice: once for HKDF and once for AES wrapping.
  // Clone it before the HKDF call which wipes the original (L0-9).
  const prfOutputForWrap = new Uint8Array(prfOutput);
  let cloneOwned = true;
  let kek: Uint8Array | null = null;
  try {
    // Derive KEK via HKDF — original prfOutput wiped inside (L0-9).
    kek = derivePrfVaultKek(prfOutput, prfSalt);

    // Ownership of the clone transfers to the consuming wrapper at this call;
    // deriveWrappingKey wipes it on every exit. Before this point it remains
    // ours, including when the primary derivation rejects.
    cloneOwned = false;
    const wrappedKek = await wrapRecoveredKekWithPrf(
      kek,
      prfOutputForWrap,
      prfSalt,
    );
    return { kek, wrappedKek };
  } catch (error) {
    // A successfully returned KEK belongs to the caller. Until return, this
    // function owns it and must wipe it when any wrapping boundary rejects.
    if (kek) atomicWipe(kek);
    throw error;
  } finally {
    if (cloneOwned) atomicWipe(prfOutputForWrap);
  }
}

/**
 * Unwrap the vault KEK for an existing user with a stored wrappedKek.
 *
 * @param wrappedKek  From server: profile.vaultKekEncrypted
 * @param prfOutput   From WebAuthn assertion. CONSUMED (wiped).
 * @param prfSalt     From server: profile.prfSalt
 */
export async function unwrapKekFromPrf(
  wrappedKek: string,
  prfOutput:  Uint8Array,
  prfSalt:    string
): Promise<Uint8Array> {
  const wrappingKey = deriveWrappingKey(prfOutput, prfSalt);
  // wrappingKey is a fresh derivation; prfOutput wiped inside deriveWrappingKey
  return unwrapKekWithPrf(wrappedKek, wrappingKey);
}

/**
 * #1014 — wrap an EXTERNALLY obtained vault KEK (e.g. reconstructed via
 * guardian SSS recovery in GuardianRecoveryFlow.tsx — NOT derived here) for
 * durable server storage, keyed to a fresh WebAuthn PRF assertion from the
 * CURRENT authenticator. Reuses the exact same domain-separated
 * wrapping-key derivation as `deriveKekFromPrf`'s wrap half and the exact
 * same wrap primitive as fresh registration — no new KEK derivation path
 * (L0-6 unchanged). A later `unwrapKekFromPrf` call with a fresh assertion
 * from the SAME authenticator + this `prfSalt` recovers the exact `kek`
 * bytes — this is what makes the vault durably openable in a fresh session.
 *
 * @param kek        The already-reconstructed 32-byte vault KEK. NOT wiped
 *                    here — caller retains ownership (Rule 3 / L0-9).
 * @param prfOutput  Fresh WebAuthn PRF extension output. CONSUMED (wiped).
 * @param prfSalt    The user's stored PRF eval salt (hex string).
 */
export async function wrapRecoveredKekWithPrf(
  kek:       Uint8Array,
  prfOutput: Uint8Array,
  prfSalt:   string
): Promise<string> {
  const wrappingKey = deriveWrappingKey(prfOutput, prfSalt);
  // wrappingKey is a fresh derivation; prfOutput wiped inside deriveWrappingKey
  return wrapKekWithPrf(kek, wrappingKey);
}

// ---------------------------------------------------------------------------
// In-memory vault key store (ephemeral — Tab/session scoped)
// ---------------------------------------------------------------------------

/** The active vault KEK held in-memory (never serialised, never persisted) */
let _activeKek: Uint8Array | null = null;

/** User id the active KEK belongs to — set with the KEK, nulled on clear. */
let _activeKekOwner: string | null = null;

/**
 * Store the vault KEK in memory for this session.
 * Called after successful authentication + PRF derivation.
 *
 * @param ownerUserId The authenticated user id this KEK was derived for —
 *   read back via getActiveKekOwner() by identity-checked probe paths.
 */
export function storeActiveKek(kek: Uint8Array, ownerUserId: string): void {
  if (_activeKek) {
    atomicWipe(_activeKek);
  }
  _activeKek = new Uint8Array(kek);
  _activeKekOwner = ownerUserId;
}

/**
 * Retrieve the active vault KEK from memory.
 * Returns null if no session is active or KEK has been wiped.
 */
export function getActiveKek(): Uint8Array | null {
  return _activeKek ? new Uint8Array(_activeKek) : null;
}

/**
 * Return the user id the active KEK was stored for (null when slot empty or
 * provenance unknown). The tag is an identifier, not key material.
 */
export function getActiveKekOwner(): string | null {
  return _activeKekOwner;
}

/**
 * The active vault KEK, but ONLY when the slot is tagged to `userId` — the
 * single home for the #1007 owner check that every KEK reader owes.
 *
 * The check is not decorative: the slot is module-level and survives a
 * tab-level account swap until something clears it, so handing user A's key
 * material to a flow running as user B is exactly the gap the owner tag exists
 * to close. Written inline at several call sites before this existed, which
 * meant a reader that forgot it was invisible.
 *
 * Returns a COPY the caller owns and MUST wipe (see `getActiveKek`); wiping it
 * never touches the live slot. A null `userId` — an unresolved session — always
 * yields null rather than falling through to an unchecked read.
 *
 * NOT a substitute for `ensureActiveKek`, which re-asserts and repopulates the
 * slot and deliberately never returns key material. Different job.
 */
export function getActiveKekFor(userId: string | null | undefined): Uint8Array | null {
  if (!userId || _activeKekOwner !== userId) return null;
  return getActiveKek();
}

/**
 * Whether the vault-KEK slot is currently populated — without materializing
 * a copy of the key material. Use this at every existence-probe call site
 * instead of `getActiveKek() !== null` (#1016): a probe that only needs a
 * yes/no answer must never allocate/return live key bytes just to discard
 * them, since a discarded copy is a wipe obligation the caller can forget.
 */
export function hasActiveKek(): boolean {
  return _activeKek !== null;
}

/**
 * Wipe the active vault KEK from memory.
 * Called from the session-death 401 path (queryClient.ts) and the
 * owner-mismatch probe (ensure-active-kek.ts). Sign-out and a Forensic Pause
 * lift reach it only indirectly — the next request is UNAUTHENTICATED and
 * takes the 401 path; logout.ts itself never calls this.
 */
export function clearActiveKek(): void {
  if (_activeKek) {
    atomicWipe(_activeKek);
    _activeKek = null;
  }
  _activeKekOwner = null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Derive the AES-256-GCM wrapping key from the PRF output.
 * Uses a different HKDF context label than the vault KEK itself.
 * The prfOutput is wiped after use.
 */
function deriveWrappingKey(prfOutput: Uint8Array, prfSalt: string): Uint8Array {
  const WRAP_INFO = "strem-vault-kek-wrap-v1";
  const saltBytes = hexToBytes(prfSalt);
  const infoBytes = new TextEncoder().encode(WRAP_INFO);
  try {
    return hkdf(sha256, prfOutput, saltBytes, infoBytes, KEK_BYTES);
  } finally {
    atomicWipe(prfOutput);
  }
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}
